import React, { useState, useEffect } from "react";
import axios from "axios";
import { AuthGuard } from "../App";
import { EmailGuard } from "../App";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilePdf, faFileCsv } from "@fortawesome/free-solid-svg-icons";
import { StandardURL } from "../App";


const PropertyPage = () => {
  const [properties, setProperties] = useState([]);
  const [searchInput, setSearchInput] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [originalProperties, setOriginalProperties] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [coordinates, setCoordinates] = useState({
    latitude: "",
    longitude: "",
  });

  useEffect(() => {
    fetchProperties();
  }, []);

  const fetchProperties = () => {
    const token = localStorage.getItem("token");
    axios
      .get(
        `${StandardURL}/iNyumba/admin/properties/recordsall`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        setProperties(response.data.response);
        setOriginalProperties(response.data.response);
      })
      .catch((error) => {
        console.error("Error fetching properties:", error);
        toast.error(error.response.data.response);
      });
  };

  const updateProperty = (propertyId) => {
    const token = localStorage.getItem("token");
    axios
      .patch(
        `${StandardURL}/iNyumba/admin/properties/updateapprovalstatus/${propertyId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        toast.success(response.data.response);
        fetchProperties();
      })
      .catch((error) => {
        console.error("Error updating property status:", error);
        toast.error(error.response.data.response);
      });
  };

  const updateMonetaryStatusProperty = (propertyId) => {
    const token = localStorage.getItem("token");
    axios
      .patch(
        `${StandardURL}/iNyumba/admin/properties/updatemonetarystatus/${propertyId}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        toast.success(response.data.response);
        fetchProperties();
      })
      .catch((error) => {
        console.error("Error updating property status:", error);
        toast.error(error.response.data.response);
      });
  };

  const deleteProperty = (propertyId) => {
    const token = localStorage.getItem("token");
    axios
      .delete(
        `${StandardURL}/iNyumba/admin/properties/delete/${propertyId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        toast.success(response.data.response);
        fetchProperties(); // Refetch properties after deletion
      })
      .catch((error) => {
        console.error("Error deleting property:", error);
        toast.error(error.response.data.response);
      });
  };

  const searchProperty = () => {
    if (!propertyId) return; // Don't search if no input
    const token = localStorage.getItem("token");
    axios
      .get(
        `${StandardURL}/iNyumba/admin/properties/records/${propertyId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        setProperties([response.data.response]);
      })
      .catch((error) => {
        console.error("Error searching property:", error);
        toast.error(error.response.data.response);
      });
  };

  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };

  const handleSearch = () => {
    setPropertyId(searchInput); // Update propertyId state before searching
    searchProperty(); // Then trigger search
  };

  const handleReset = () => {
    setPropertyId("");
    setSearchInput("");
    setProperties(originalProperties);
  };

  const handleDownload = (fileType) => {
    const endpoint = `${StandardURL}/iNyumba/report/propertiesviewreports`;
    const token = localStorage.getItem("token");

    // Fetch and download file
    axios
      .get(endpoint, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
        params: { format: fileType },
        responseType: "blob", // Important for file download
      })
      .then((response) => {
        const fileExtension = fileType === "pdf" ? "pdf" : "csv";
        const fileName = `inyumbapaidbankrecords.${fileExtension}`;
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", fileName);
        document.body.appendChild(link);
        link.click();
        toast.success("Download is successfull");
      })
      .catch((error) => {
        console.error(`Error downloading ${fileType.toUpperCase()}:`, error);
        toast.error(error.response.data.response);
      });
  };

  const handleAddCoordinates = () => {
    const token = localStorage.getItem("token");

    axios
      .patch(
        `${StandardURL}/iNyumba/admin/properties/updatecoordinates/${propertyId}`,
        { coordinates }, // Sending the coordinates as JSON
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
      .then((response) => {
        toast.success(response.data.response);
        fetchProperties(); // Refresh the properties list
        setIsModalOpen(false); // Close the modal
      })
      .catch((error) => {
        console.error("Error updating coordinates:", error);
        toast.error(error.response.data.response);
      });
  };

  const renderModal = () => {
    if (!isModalOpen) return null;

    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 backdrop-blur-sm">
        <div className="w-full max-w-sm transform rounded-2xl bg-white p-8 shadow-2xl transition-all duration-300">
          <h2 className="mb-6 text-2xl font-bold text-gray-800">
            Add Coordinates
          </h2>
          <input
            type="text"
            placeholder="Latitude"
            value={coordinates.latitude}
            onChange={(e) =>
              setCoordinates({ ...coordinates, latitude: e.target.value })
            }
            className="mb-4 w-full rounded-lg border border-gray-300 px-4 py-2 text-gray-700 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
          />
          <input
            type="text"
            placeholder="Longitude"
            value={coordinates.longitude}
            onChange={(e) =>
              setCoordinates({ ...coordinates, longitude: e.target.value })
            }
            className="mb-4 w-full rounded-lg border border-gray-300 px-4 py-2 text-gray-700 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
          />
          <div className="mt-6 flex justify-end space-x-3">
            <button
              onClick={() => setIsModalOpen(false)}
              className="rounded-full bg-gray-400 px-6 py-2 font-semibold text-white transition-colors duration-200 hover:bg-gray-500"
            >
              Cancel
            </button>
            <button
              onClick={handleAddCoordinates}
              className="rounded-full bg-sky-500 px-6 py-2 font-semibold text-white transition-colors duration-200 hover:bg-sky-600"
            >
              Post
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <EmailGuard>
      <AuthGuard>
        <div className="min-h-screen bg-gray-100 p-8">
          <div className="container mx-auto max-w-7xl rounded-2xl bg-white p-8 shadow-2xl">
            <h1 className="mb-6 text-4xl font-extrabold text-gray-900">
              Properties Overview
            </h1>
            <div className="mb-6 flex flex-wrap items-center gap-4">
              <div className="flex w-full gap-2 sm:w-auto">
                <input
                  type="text"
                  placeholder="Enter Property ID"
                  value={searchInput}
                  onChange={handleSearchInputChange}
                  className="min-w-[200px] flex-grow rounded-full border border-gray-300 px-4 py-2 text-gray-800 focus:border-sky-500 focus:outline-none focus:ring-1 focus:ring-sky-500"
                />
                <button
                  onClick={handleSearch}
                  className="transform rounded-full bg-sky-500 px-6 py-2 font-semibold text-white shadow-md transition-all duration-200 hover:scale-105 hover:bg-sky-600"
                >
                  Search
                </button>
                <button
                  onClick={handleReset}
                  className="transform rounded-full bg-gray-400 px-6 py-2 font-semibold text-white shadow-md transition-all duration-200 hover:scale-105 hover:bg-gray-500"
                >
                  Reset
                </button>
              </div>
              <div className="flex flex-grow justify-end space-x-2">
                <button
                  onClick={() => handleDownload("csv")}
                  className="transform rounded-full bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:scale-105 hover:bg-green-700"
                >
                  <FontAwesomeIcon icon={faFileCsv} className="mr-2" />
                  Download CSV
                </button>
                <button
                  onClick={() => handleDownload("pdf")}
                  className="transform rounded-full bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-md transition-all duration-200 hover:scale-105 hover:bg-red-700"
                >
                  <FontAwesomeIcon icon={faFilePdf} className="mr-2" />
                  Download PDF
                </button>
              </div>
            </div>
            <div className="overflow-x-auto">
              <table className="min-w-full table-auto text-sm">
                <thead>
                  <tr className="bg-gray-50 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                    <th className="px-4 py-3">Property ID</th>
                    <th className="px-4 py-3">Name</th>
                    <th className="px-4 py-3">Owner Id</th>
                    <th className="px-4 py-3">Approval Status</th>
                    <th className="px-4 py-3">Monetary Status</th>
                    <th className="px-4 py-3">Coordinates</th>
                    <th className="px-4 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {properties.map((property) => (
                    <tr
                      key={property.id}
                      className="transition-colors duration-200 hover:bg-sky-50"
                    >
                      <td className="whitespace-nowrap px-4 py-4 font-semibold text-gray-900">
                        {property.property_id}
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 text-gray-700">
                        {property.title}
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 text-gray-700">
                        {property.owner_id}
                      </td>
                      <td className="whitespace-nowrap px-4 py-4">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase ${
                            property.approvalstatus === "Approved"
                              ? "bg-green-100 text-green-800"
                              : "bg-yellow-100 text-yellow-800"
                          }`}
                        >
                          {property.approvalstatus}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-4">
                        <span
                          className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold uppercase ${
                            property.monetarystatus === "Paid"
                              ? "bg-green-100 text-green-800"
                              : "bg-red-100 text-red-800"
                          }`}
                        >
                          {property.monetarystatus}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 text-gray-700">
                        {property.coordinates || "N/A"}
                      </td>
                      <td className="whitespace-nowrap px-4 py-4 text-right">
                        <div className="flex justify-center space-x-2">
                          <button
                            onClick={() => updateProperty(property.property_id)}
                            className="transform rounded-full bg-green-500 px-3 py-1 text-xs font-semibold text-white shadow-sm transition-all duration-200 hover:scale-105 hover:bg-green-600"
                          >
                            Update Approval
                          </button>
                          <button
                            onClick={() =>
                              updateMonetaryStatusProperty(property.property_id)
                            }
                            className="transform rounded-full bg-yellow-500 px-3 py-1 text-xs font-semibold text-white shadow-sm transition-all duration-200 hover:scale-105 hover:bg-yellow-600"
                          >
                            Update Monetary Status
                          </button>
                          <button
                            onClick={() => {
                              setPropertyId(property.property_id);
                              setIsModalOpen(true);
                            }}
                            className="transform rounded-full bg-blue-500 px-3 py-1 text-xs font-semibold text-white shadow-sm transition-all duration-200 hover:scale-105 hover:bg-blue-600"
                          >
                            Add Coordinates
                          </button>
                          <button
                            onClick={() => deleteProperty(property.property_id)}
                            className="transform rounded-full bg-red-500 px-3 py-1 text-xs font-semibold text-white shadow-sm transition-all duration-200 hover:scale-105 hover:bg-red-600"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          {renderModal()}
        </div>
      </AuthGuard>
    </EmailGuard>
  );
};

export default PropertyPage;
